import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import LoadingSpinner from "../Reports/EditReports/EditTables/loader";
import EnptReportDataGrid from "./EnptReportDataGrid";
import EnptEdrTable from "./EnptEdrTable";
import EnptKpiTable from "./EnptKpiTable";
import EnptKpiCharts from "./EnptKpiCharts";
import { useDeleteReportDayMutation, useLazyGetEnptReportDataQuery, useUpdateEnptKpiMutation } from "store/enptReport/enptReport.api";
import Swal from "sweetalert2";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import jwt from "jsonwebtoken";

const EnptReportDetailsTab = ({ reportData, loading, setLoading, refetchReport, refetchList, customers }) => {
  const decodeToken = token => {
    try {
      return jwt.decode(token);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const token = localStorage.getItem("authUser");
  const decodedToken = token ? decodeToken(JSON.parse(token)) : null;
  const role = decodedToken?.role;

  const [activeTab, setActiveTab] = useState("1");
  const [deleteReportDay] = useDeleteReportDayMutation();
  const [fetchReportData, { data: lazyReportData, isFetching: isLoadingRows }] = useLazyGetEnptReportDataQuery();
  const [updateEnptKpi] = useUpdateEnptKpiMutation();

  const kpiDataRef = React.useRef([]);

  React.useEffect(() => {
    if (reportData?.enpt_kpis) {
      // Deep clone to allow mutable in-place edits by react-bootstrap-table-next
      kpiDataRef.current = JSON.parse(JSON.stringify(reportData.enpt_kpis));
    } else {
      kpiDataRef.current = [];
    }
  }, [reportData?.enpt_kpis]);

  const handleSaveKpiEdits = async () => {
    try {
      setLoading(true);
      await updateEnptKpi({ 
        id: reportData.id, 
        data: kpiDataRef.current 
      }).unwrap();
      
      toastr.success("Values Updated", "Edit Successful");
      refetchReport();
    } catch (err) {
      console.error("Failed to update KPI:", err);
      Swal.fire('Error', 'Failed to update KPI data', 'error');
      setLoading(false);
    }
  };

  // Extract unique dates from KPIs (kept for delete-day functionality and other tabs)
  const dates = reportData?.enpt_kpis 
    ? [...new Set(reportData.enpt_kpis.map(k => k.test_date))].sort() 
    : [];

  // ── DATE FILTER (commented out for Reports tab — now shows ALL records) ──
  // const [selectedDate, setSelectedDate] = useState(null);
  //
  // // Set initial selected date when data loads, then lazy-fetch rows
  // React.useEffect(() => {
  //   if (dates.length > 0) {
  //     const newDate = dates.includes(selectedDate) ? selectedDate : dates[0];
  //     if (newDate !== selectedDate) setSelectedDate(newDate);
  //   }
  // }, [JSON.stringify(dates)]);
  //
  // // Fetch rows whenever selected date or report id changes
  // React.useEffect(() => {
  //   if (selectedDate && reportData?.id) {
  //     console.log(`[EnptReportDetailsTab] Fetching rows for report ${reportData.id} date ${selectedDate}`);
  //     fetchReportData({ id: reportData.id, date: selectedDate, limit: 500 });
  //   }
  // }, [selectedDate, reportData?.id]);

  // ── NEW: Fetch ALL records for the report (no date filter) when report changes ──
  React.useEffect(() => {
    if (reportData?.id) {
      console.log(`[EnptReportDetailsTab] Fetching ALL rows for report ${reportData.id}`);
      fetchReportData({ id: reportData.id, limit: 5000 });
    }
  }, [reportData?.id]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleDeleteDay = async (date) => {
    const isLastDay = dates.length <= 1;
    const confirmText = isLastDay 
      ? `This is the last day in this report. Deleting it will delete the entire report. Are you sure?`
      : `Are you sure you want to delete all records, KPIs, and EDR data for ${date}?`;

    const result = await Swal.fire({
      title: 'Delete Day?',
      text: confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await deleteReportDay({ id: reportData.id, date }).unwrap();
        Swal.fire('Deleted!', `Data for ${date} has been deleted.`, 'success');
        if (isLastDay) {
          // If it was the last day, refetching might return 404 or empty, 
          // usually the parent component handles it or we just redirect.
          window.location.reload(); 
        } else {
          refetchReport();
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to delete day', 'error');
        setLoading(false);
      }
    }
  };

  if (loading || !reportData) {
    return (
      <div className="text-center my-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="col-12" style={{ paddingTop: "5px" }}>
      <Container fluid={true}>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div
                  className="ps-3 pe-3"
                  style={{
                    display: "flex",
                  }}
                >
                  <Nav
                    pills
                    className="navtab-bg nav-justified"
                    style={{ marginRight: "10px", width: "25%" }}
                  >
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: activeTab === "1",
                        })}
                        onClick={() => toggleTab("1")}
                      >
                        Reports
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <Nav
                    pills
                    className="navtab-bg nav-justified"
                    style={{ marginRight: "10px", width: "25%" }}
                  >
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: activeTab === "2",
                        })}
                        onClick={() => toggleTab("2")}
                      >
                        Enterprise Delivery Ratio
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <Nav
                    pills
                    className="navtab-bg nav-justified"
                    style={{ marginRight: "10px", width: "25%" }}
                  >
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: activeTab === "3",
                        })}
                        onClick={() => toggleTab("3")}
                      >
                        KPI Table
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <Nav
                    pills
                    className="navtab-bg nav-justified"
                    style={{ marginRight: "0px", width: "25%" }}
                  >
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: activeTab === "4",
                        })}
                        onClick={() => toggleTab("4")}
                      >
                        KPI Charts
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>

                <TabContent activeTab={activeTab} className="p-3 text-muted">
                  <TabPane tabId="1">
                    {/* ── DATE SELECTOR hidden — Reports tab now shows ALL records ──
                    {dates.length > 0 && (
                      <div className="mb-3 d-flex align-items-center flex-wrap gap-2">
                        <span className="fw-bold me-2">Select Date:</span>
                        {dates.map(date => (
                          <div key={date} className="btn-group">
                            <button 
                              className={`btn btn-sm ${selectedDate === date ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setSelectedDate(date)}
                            >
                              {date}
                            </button>
                            <button 
                              className="btn btn-sm btn-danger" 
                              title={`Delete data for ${date}`}
                              onClick={() => handleDeleteDay(date)}
                            >
                              <i className="mdi mdi-delete"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    ── END DATE SELECTOR ── */}
                    {isLoadingRows && (
                      <div className="text-center py-3"><LoadingSpinner /></div>
                    )}
                    {!isLoadingRows && (
                      <Row>
                        <Col>
                          <EnptReportDataGrid
                            reportData={lazyReportData?.rows || []}
                            selectedDate={null}
                            setLoading={setLoading}
                            refetchReport={refetchReport}
                            refetchRows={() => fetchReportData({ id: reportData.id, limit: 5000 })}
                            refetchList={refetchList}
                            customers={customers}
                            reportCustomer={reportData?.customer}
                            initialHighlights={lazyReportData?.highlights || []}
                            reportId={reportData.id}
                          />
                        </Col>
                      </Row>
                    )}
                  </TabPane>
                  <TabPane tabId="2">
                    <Row>
                      <Col>
                        <EnptEdrTable edrs={reportData.enpt_edrs} reportId={reportData.id} />
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="3">
                    <Row className="mb-3">
                      <Col className="d-flex justify-content-end">
                        {[2, 3, 5].includes(role) && (
                          <button 
                            className="btn btn-danger"
                            onClick={handleSaveKpiEdits}
                          >
                            Save Changes
                          </button>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <EnptKpiTable kpis={kpiDataRef.current} reportId={reportData.id} />
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="4">
                    <Row>
                      <Col>
                        <EnptKpiCharts kpis={reportData.enpt_kpis} />
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EnptReportDetailsTab;
